import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CreditCard } from 'lucide-react'
import toast from 'react-hot-toast'
import { billingService, type BillingStatus } from '@/services/billing'
import { useAuthStore } from '@/store/authStore'

export default function BillingPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [status, setStatus] = useState<BillingStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)

  useEffect(() => {
    billingService
      .getStatus()
      .then((s) => {
        setStatus(s)
        if (!s.requires_payment_setup && (s.payment_method_on_file || s.trial_active)) {
          // Already good — don't trap them here.
        }
      })
      .catch(() => toast.error('Could not load billing status'))
      .finally(() => setLoading(false))
  }, [])

  const startCheckout = async () => {
    setStarting(true)
    try {
      const { url } = await billingService.createCheckoutSession()
      window.location.href = url
    } catch (error: any) {
      const payload = error.response?.data
      toast.error(
        (typeof payload === 'string' ? payload : payload?.message) ||
          'Card checkout is not configured yet. You can keep using the 30-day trial.'
      )
      setStarting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-lg w-full bg-white shadow rounded-lg p-8">
        <div className="mx-auto h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center mb-4">
          <CreditCard className="h-6 w-6 text-primary-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 text-center">Start your 30-day free trial</h1>
        <p className="mt-3 text-sm text-gray-600 text-center">
          Add a card to keep your workspace after the trial. You will not be charged until
          {status?.trial_ends_at
            ? ` ${new Date(status.trial_ends_at).toLocaleDateString()}.`
            : ' the trial ends.'}
        </p>
        {user?.email && (
          <p className="mt-2 text-xs text-gray-500 text-center">Signed in as {user.email}</p>
        )}
        {status && (
          <dl className="mt-6 text-sm text-gray-700 space-y-2">
            <div className="flex justify-between">
              <dt>Status</dt>
              <dd className="font-medium">{status.billing_status}</dd>
            </div>
            <div className="flex justify-between">
              <dt>Days left in trial</dt>
              <dd className="font-medium">{status.days_left_in_trial}</dd>
            </div>
            <div className="flex justify-between">
              <dt>Card on file</dt>
              <dd className="font-medium">{status.payment_method_on_file ? 'Yes' : 'No'}</dd>
            </div>
          </dl>
        )}
        <button
          onClick={startCheckout}
          disabled={starting || !status?.stripe_configured}
          className="mt-6 w-full py-2 px-4 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
        >
          {starting ? 'Redirecting to Stripe…' : 'Add card and start trial'}
        </button>
        {!status?.stripe_configured && (
          <p className="mt-3 text-xs text-amber-700 text-center">
            Card collection is not wired in this environment yet. Your 30-day trial is already running.
          </p>
        )}
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-3 w-full py-2 px-4 rounded-md text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
        >
          Continue to dashboard
        </button>
      </div>
    </div>
  )
}
